import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Dialog, Slide, DialogTitle, DialogContent, Modal, AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  ButtonGroup,
  Box,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { useSnackbar } from 'notistack';
import { useS3WithPresignedUrls } from '../hooks/useS3WithPreSignedUrls';
import { useDuckDBIntegration } from '../hooks/useDuckDBIntegration';
import { useSourceDataLoader } from '../hooks/useSourceDataLoader';
import { useMappingWebSocket } from '../hooks/useMappingWebSocket';
import { useGenerateSqlWebSocket } from '../hooks/useGenerateSqlWebSocket';
import ModalContent from '../components/ModalContent';
import { INITIAL_AI_SUGGESTIONS } from '../constants';
import { buildSampleSchema, buildProfileData } from '../utils/schemaSampler';
import generateAlias from '../../../../utils/generateAlias';
import { registerTablesWithDuckDB } from '../utils/duckdbCsvRegistrar';
import { sanitizeGeneratedSql } from '../utils/sanitizeGeneratedSql';
import AutoMapperInfoImage from '../../../../assets/images/Auto-mapper-info-image.png';
import useDebounce from '../../../../hooks/useDebounce';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const NodeModal_AutoMapperV2 = ({
  nodeId,
  nodeName,
  handleClose,
  open,
  nodes,
  edges,
  changeNodeName,
  setNodes,
  workflow,
  dataModel = false,
  dataModelformField,
  attributes,
  setDataModelformField = () => { },
}) => {
  const { enqueueSnackbar } = useSnackbar();

  // Use DuckDB integration hook
  const {
    isInitialized,
    duckDBLoading,
    duckDBError,
    duckDBStatus,
    duckDBLogs,
    testQueryResult,
    addLog,
    testDuckDBConnection,
    profileSourceData,
    connection,
    executeQuery,
    registerFile,
  } = useDuckDBIntegration(open);

  const INITIALSTATE = {
    df: [],
    sqlQuery: '',
    alias: generateAlias(nodeName, nodeId),
  };

  const [formField, setFormField] = useState(INITIALSTATE);
  const [disableForm, setDisableForm] = useState(false);
  const [headerName, setHeaderName] = useState([]);
  const [fetchedHeader, setFetchedHeader] = useState([]);
  const [tempHeader, setTempHeader] = useState([]);
  const [templateData, setTemplateData] = useState([]);
  const [aliasError, setAliasError] = useState('');
  const [headers, setHeaders] = useState([]);
  const [columnErrors, setColumnErrors] = useState({});
  const { getSampleData, profileWithPresignedUrl, loading, progress } = useS3WithPresignedUrls();
  const [profiling, setProfiling] = useState(false);
  const [profiles, setProfiles] = useState({});
  const [sources, setSources] = useState([]);
  const [finalPayload, setFinalPayload] = useState(null);
  const [sourceProfileData, setSourceProfileData] = useState([]);
  const [targetProfileData, setTargetProfileData] = useState([]);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [schemaInfoModalOpen, setSchemaInfoModalOpen] = useState(false);

  const [targetSchema, setTargetSchema] = useState([]);
  const [mode, setMode] = useState('Design-time (AI Assisted)');
  const [aiProvider, setAiProvider] = useState('Bedrock / Foundry');
  const [mappings, setMappings] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState(INITIAL_AI_SUGGESTIONS);
  const [previewTables, setPreviewTables] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [currentSql, setCurrentSql] = useState(null);
  const latestSqlRef = useRef('');


  const {
    mappings: wsMappings,
    loading: wsLoading,
    complete: mappingComplete,
    sendPayload: sendMappingPayload,
  } = useMappingWebSocket();

  const {
    sendPayload: sendGenerateSqlPayload,
    pysparkSql,
    duckdbSql: generateSqlDuckdbSql,
    status: generateSqlStatus,
    lastMessage: generateSqlMessage,
  } = useGenerateSqlWebSocket();

  useEffect(() => {
    if (!dataModel) return;

    latestSqlRef.current =
      pysparkSql ||
      mappingComplete?.pyspark_sql ||
      formField.sqlQuery ||
      '';

    sessionStorage.setItem("data_model_sql_query", JSON.stringify(latestSqlRef.current));

  }, [pysparkSql, mappingComplete?.pyspark_sql]);


  const isPreviewAvailable = useMemo(() => {
    return currentSql !== null && isInitialized && !duckDBLoading;
  }, [currentSql, isInitialized, duckDBLoading]);

  // Preview button is always enabled when DuckDB is initialized and SQL is available
  const isPreviewButtonEnabled = isPreviewAvailable && !previewLoading;

  // While streaming, show wsMappings directly so each new mapping renders immediately (no sync delay)
  const prevWsLoadingRef = useRef(wsLoading);
  const userHasModifiedMappingsRef = useRef(false);
  const userHasDeletedMappingRef = useRef(false);
  const wsLengthWhenUserModifiedRef = useRef(0);

  useEffect(() => {
    if (prevWsLoadingRef.current === false && wsLoading === true) {
      userHasModifiedMappingsRef.current = false;
      userHasDeletedMappingRef.current = false;
      wsLengthWhenUserModifiedRef.current = 0;
    }
    // When loading finishes (e.g. after Regenerate or initial request), sync local mappings from WebSocket result
    if (prevWsLoadingRef.current === true && wsLoading === false) {
      setMappings(Array.isArray(wsMappings) ? wsMappings : []);
    }
    prevWsLoadingRef.current = wsLoading;
  }, [wsLoading, wsMappings]);

  // When user has modified (e.g. edited/added) but not deleted, append newly streamed mappings
  // so progress doesn't get stuck. When user has deleted, do not merge — keep their reduced list.
  useEffect(() => {
    if (
      !userHasModifiedMappingsRef.current ||
      userHasDeletedMappingRef.current ||
      !wsLoading ||
      !Array.isArray(wsMappings)
    )
      return;
    const mergedUpTo = wsLengthWhenUserModifiedRef.current;
    if (wsMappings.length <= mergedUpTo) return;
    const newItems = wsMappings.slice(mergedUpTo);
    wsLengthWhenUserModifiedRef.current = wsMappings.length;
    setMappings((prev) => [...(Array.isArray(prev) ? prev : []), ...newItems]);
  }, [wsLoading, wsMappings]);

  const displayMappings = userHasModifiedMappingsRef.current
    ? Array.isArray(mappings)
      ? mappings
      : []
    : wsLoading
      ? Array.isArray(wsMappings)
        ? wsMappings
        : []
      : Array.isArray(mappings)
        ? mappings
        : [];
  const displayMappingsRef = useRef(displayMappings);
  displayMappingsRef.current = displayMappings;
  const existingMappings = useMemo(() => displayMappings, [displayMappings]);
  const totalTargetColumns = useMemo(() => {
    if (!Array.isArray(targetSchema)) return 0;
    return targetSchema.reduce((acc, t) => acc + (Array.isArray(t.columns) ? t.columns.length : 0), 0);
  }, [targetSchema]);

  // Loader can call setMappings; we don't use it for UI (mappings come from wsMappings)
  const setJoinsNoop = useCallback(() => { }, []);

  // Use source data loader hook
  const { getLocalData } = useSourceDataLoader(
    nodeId,
    edges,
    isInitialized,
    connection,
    profileSourceData,
    addLog,
    setFetchedHeader,
    setHeaders,
    setFormField,
    setHeaderName,
    setTemplateData,
    setDisableForm,
    setJoinsNoop,
    setMappings,
    setTargetSchema,
    setSources,
    dataModel,
    dataModelformField,
    setDataModelformField,
    attributes
  );

  // Resolve which SQL to use (from mapping complete or generate SQL)
  const resolvedDuckdbSql = useMemo(() => {
    // Priority 1: Newly generated SQL from generate-sql WebSocket
    if (generateSqlDuckdbSql) return generateSqlDuckdbSql;

    // Priority 2: SQL from mapping WebSocket
    if (mappingComplete?.duckdb_sql) return mappingComplete.duckdb_sql;

    return null;
  }, [generateSqlDuckdbSql, mappingComplete]);

  // Update current SQL when resolved SQL changes
  useEffect(() => {
    if (resolvedDuckdbSql) {
      setCurrentSql(resolvedDuckdbSql);

      // Auto-run preview when new SQL is available
      if (isInitialized && !previewLoading) {
        runPreviewQuery(resolvedDuckdbSql);
      }
    }
  }, [resolvedDuckdbSql, isInitialized]);

const makeBackendDateSqlPreviewSafe = (sql) => {
  return sql.replace(
    /CAST\s*\(\s*strptime\s*\(\s*([^)]+?)\s*,\s*'([^']+)'\s*\)\s*AS\s+TIMESTAMP\s*\)/gi,
    (_, colExpr, originalFormat) => `
CAST(
  STRFTIME(
    COALESCE(
      TRY_STRPTIME(CAST(${colExpr} AS VARCHAR), '${originalFormat}'),
      TRY_STRPTIME(CAST(${colExpr} AS VARCHAR), '%Y-%m-%d'),
      TRY_STRPTIME(CAST(${colExpr} AS VARCHAR), '%d-%m-%Y'),
      TRY_STRPTIME(CAST(${colExpr} AS VARCHAR), '%m-%d-%Y'),
      TRY_STRPTIME(CAST(${colExpr} AS VARCHAR), '%d/%m/%Y'),
      TRY_STRPTIME(CAST(${colExpr} AS VARCHAR), '%Y/%m/%d')
    ),
    '${originalFormat}'
  )
AS VARCHAR)
`
  );
};

  // Function to run preview query
  const runPreviewQuery = async (sql) => {
    if (!sql || !isInitialized) {
      console.log('⚠️ Cannot run preview: SQL or DuckDB not ready');
      return;
    }

    try {
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewData([]);

      const tables = await executeQuery('SHOW TABLES;');
      setPreviewTables(tables);

      const cleanedSql = await sanitizeGeneratedSql(sql, executeQuery);

      const safeSql = makeBackendDateSqlPreviewSafe(cleanedSql);

      const rows = await executeQuery(safeSql);

      if (!Array.isArray(rows)) {
        throw new Error('Query returned invalid data format');
      }

      if (rows.length === 0) {
        setPreviewData([]);
        enqueueSnackbar('Preview query executed but returned no rows', { variant: 'info' });
      } else {
        setPreviewData(rows);
      }
    } catch (err) {
      console.error('❌ Preview Execution Error:', err);

      setPreviewData([]);
      setPreviewError('Failed to execute query');

      enqueueSnackbar(`Preview failed`, { variant: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Manual preview handler
  const handlePreview = async () => {
    if (!currentSql) {
      enqueueSnackbar('No SQL query available for preview', { variant: 'warning' });
      return;
    }

    if (!isInitialized) {
      enqueueSnackbar('DuckDB not initialized', { variant: 'error' });
      return;
    }

    await runPreviewQuery(currentSql);

    // Open preview dialog automatically if we have data or want to show error
    setOpenPreviewDialog(true);

    const previewPayload = {
      nodeId,
      nodeName,
      step_name: nodeName,
      formField,
      headerName: fetchedHeader,
      templateData,
      disabled: false,
      mappings: displayMappings,
      sources,
      targetSchema,
      duckDBEnabled: isInitialized,
    };

    console.debug('Auto-mapper preview payload', previewPayload);
  };

  useEffect(() => {
    if (Array.isArray(sources) && sources.length > 0 && Array.isArray(targetSchema) && targetSchema.length > 0) {
      const sourceSchemas = sources.map((src) =>
        buildSampleSchema({
          entityName: src.name || src.id,
          columns: (src.columns || []).map((col) => ({
            name: col,
            type: src.dataTypes?.[col] || 'string',
          })),
          sampleRows: src.sampleRows || [],
          type: 'source',
        })
      );

      const destinationSchemas = targetSchema.map((dest) =>
        buildSampleSchema({
          entityName: dest.tableName || dest.id,
          columns: dest.columns || [],
          sampleRows: dest.sampleRows || [],
          type: 'destination',
        })
      );

      setFinalPayload({
        sources: sourceSchemas,
        destinations: destinationSchemas,
      });

      // 🔹 PROFILE DATA (UI)
      const sourceProfiles = sources.map((src) =>
        buildProfileData({
          entityName: src.name || src.id,
          columns: (src.columns || []).map((col) => ({
            name: col,
            type: src.dataTypes?.[col] || 'string',
          })),
          sampleRows: src.sampleRows || [],
          type: 'source',
        })
      );

      const targetProfiles = targetSchema.map((dest) =>
        buildProfileData({
          entityName: dest.tableName || dest.id,
          columns: dest.columns || [],
          sampleRows: dest.sampleRows || [],
          type: 'destination',
        })
      );

      setSourceProfileData(sourceProfiles);
      setTargetProfileData(targetProfiles);
    }
  }, [sources, targetSchema]);

  useEffect(() => {
    if (!open) return;
    if (!isInitialized) return;
    if (!sources?.length) return;

    registerTablesWithDuckDB({
      sources,
      targetSchema,
      isInitialized,
      executeQuery,
      registerFile,
    });
  }, [open, isInitialized, sources, targetSchema]);

  const refreshAllState = () => {
    getLocalData();
    if (isInitialized) {
      testDuckDBConnection();
    }
    enqueueSnackbar('Schema refreshed successfully', { variant: 'success' });
  };

  const handleResetForm = () => {
    setMappings([]);
    getLocalData();
  };

  const debouncedFormField = useDebounce(formField, 200);
  useEffect(() => {
    if (!dataModel) return;

    const newLlmMapper = {
      nodeId,
      nodeName,
      formField: { ...debouncedFormField, sqlQuery: pysparkSql || mappingComplete?.pyspark_sql || formField.sqlQuery || '', },
      step_name: nodeName,
    };
    const prevLlmMapper = dataModelformField?.llmMapper;

    if (
      JSON.stringify(prevLlmMapper?.formField) === JSON.stringify(newLlmMapper.formField)
    ) {
      return;
    }

    setDataModelformField((prev) => ({
      ...prev,
      llmMapper: newLlmMapper,
    }));
  }, [debouncedFormField, dataModel]);


  const hasAutoRequestedMapping = useRef(false);

  // Load data when modal opens; clear state only when modal closes (not in cleanup to avoid Strict Mode clearing on first open)
  useEffect(() => {
    if (open) {
      getLocalData();
      if (isInitialized) {
        testDuckDBConnection();
      }
      hasAutoRequestedMapping.current = false;
    } else {
      setSources([]);
      setMappings([]);
      setFinalPayload(null);
      setCurrentSql(null);
      setPreviewData([]);
      setPreviewError(null);
      hasAutoRequestedMapping.current = false;
    }
  }, [open, nodeId, edges, isInitialized]);

  // When user has previously submitted mappings for this node, hydrate them from sessionStorage
  useEffect(() => {
    if (!open) return;
    try {
      const raw = sessionStorage.getItem('allNodes');
      if (!raw) return;
      const store = JSON.parse(raw);
      if (!Array.isArray(store)) return;
      const existing = store.find((node) => node.nodeId === nodeId);
      if (!existing) return;
      if (existing.disabled === true) {
        setDisableForm(true);
      }
      if (existing.formField && typeof existing.formField === 'object') {
        setFormField((prev) => ({
          ...prev,
          ...existing.formField,
          df: existing.formField.df ?? prev.df,
          sqlQuery: existing.formField.sqlQuery ?? prev.sqlQuery,
        }));
      }
      if (Array.isArray(existing.mappings) && existing.mappings.length > 0) {
        setMappings(existing.mappings);
        userHasModifiedMappingsRef.current = true;
        hasAutoRequestedMapping.current = true; // don't auto-request new AI mappings over saved ones
        if (existing.previewSql) {
          setCurrentSql(existing.previewSql);
        }

        if (Array.isArray(existing.previewData) && existing.previewData.length > 0) {
          setPreviewData(existing.previewData);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error hydrating saved auto-mapper mappings:', err);
    }
  }, [open, nodeId]);

  // Auto-trigger mapping request when modal opens and payload is ready; defer so it runs after mount (fixes first-open mappings not visible)
  // Only send when finalPayload has sources or destinations, so we don't fire once with {} and block retry when getLocalData finishes
  useEffect(() => {
    if (!open) return;
    if (hasAutoRequestedMapping.current) return;
    const hasPayload = finalPayload?.sources?.length || finalPayload?.destinations?.length;
    if (!hasPayload) return;
    const timeoutId = setTimeout(() => {
      hasAutoRequestedMapping.current = true;
      sendMappingPayload(finalPayload);
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [open, finalPayload, sendMappingPayload]);

  const handleEdit = () => {
    setDisableForm(false);
  };

  // Save mappings handler - triggers SQL generation
  const handleSaveMappings = () => {
    const updatedMappings = Array.isArray(mappings) ? mappings : [];
    const sourcesForWs = finalPayload?.sources ?? [];
    const destinationsForWs = finalPayload?.destinations ?? [];

    console.log(' Saving mappings:', updatedMappings.length);
    console.log(' WebSocket payload:', { sources: sourcesForWs, destinations: destinationsForWs });

    const generateSqlPayload = {
      sources: sourcesForWs,
      destinations: destinationsForWs,
      mappings: updatedMappings,
    };

    sendGenerateSqlPayload(generateSqlPayload);
    enqueueSnackbar('Generating SQL from mappings...', { variant: 'success' });
  };

  const handleFormsubmit = (e) => {
    if (e) {
      e.preventDefault();
    }

    if (wsLoading) {
      enqueueSnackbar('Please wait while AutoMapper is mapping columns…', { variant: 'info' });
      return;
    }

    if (targetSchema.length === 0) {
      enqueueSnackbar('Please add a target schema to save the node.', { variant: 'error' });
      return;
    }

    const store = JSON.parse(sessionStorage.getItem('allNodes') || '[]');
    const filteredStore = store.filter((node) => node.nodeId !== nodeId);

    // Build df array from all nodes connected as inputs to this Auto Mapper (incoming edges)
    const sourceNodeIds = (edges || []).filter((edge) => edge.target === nodeId).map((edge) => edge.source);
    const connectedDf = sourceNodeIds
      .map((srcId) => {
        const node = store.find((n) => n.nodeId === srcId);
        if (!node?.formField) return null;
        const alias = node.formField.alias;
        const df = node.formField.df;
        if (alias) return alias;
        if (Array.isArray(df)) return df.length > 0 ? df[0] : null;
        if (typeof df === 'string') return df;
        return null;
      })
      .filter(Boolean);

    console.log(pysparkSql);
    const formFieldToSave = {
      ...formField,
      df: connectedDf,
      sqlQuery: pysparkSql || formField.sqlQuery || '',
    };

    const sendFormData = {
      nodeId,
      nodeName,
      step_name: nodeName,
      formField: formFieldToSave,
      headerName: fetchedHeader,
      templateData,
      disabled: true,
      mappings,
      sources,
      targetSchema,
      duckDBEnabled: isInitialized,
      previewSql: currentSql || null,
      previewData: sanitizeForStorage(previewData || []),
    };

    filteredStore.push(sendFormData);

    sessionStorage.setItem('allNodes', JSON.stringify(filteredStore));

    const savedNodes = JSON.parse(sessionStorage.getItem('saved_node') || '[]');

    if (!savedNodes.includes(nodeId)) {
      savedNodes.push(nodeId);
      sessionStorage.setItem('saved_node', JSON.stringify(savedNodes));
    }

    setDisableForm(true);

    enqueueSnackbar('Node configuration saved successfully.', {
      variant: 'success',
    });
  };

  const sanitizeForStorage = (obj) => {
    return JSON.parse(JSON.stringify(obj, (_, value) => (typeof value === 'bigint' ? value.toString() : value)));
  };

  // Surface generate-sql WebSocket progress/completion to the user
  useEffect(() => {
    if (!generateSqlMessage || !generateSqlMessage.type) return;
    if (generateSqlMessage.type === 'progress') {
      enqueueSnackbar('Query generation in progress...', { variant: 'success' });
    } else if (generateSqlMessage.type === 'complete') {
      enqueueSnackbar('Preview data available from generated SQL.', { variant: 'success' });
    }
  }, [generateSqlMessage, enqueueSnackbar]);

  // AI request handler: send finalPayload (sources + destinations) to match-columns WebSocket
  const handleRequestAI = () => {
    if (finalPayload?.sources?.length || finalPayload?.destinations?.length) {
      sendMappingPayload(finalPayload);
      enqueueSnackbar('Requesting mapping suggestions...', { variant: 'info' });
    } else {
      enqueueSnackbar('Add sources and target schema to get mapping suggestions.', { variant: 'warning' });
    }
  };

  // Regenerate mappings: call mapping WebSocket again and replace current mappings with new suggestions
  const handleRegenerateMappings = () => {
    if (!finalPayload?.sources?.length && !finalPayload?.destinations?.length) {
      enqueueSnackbar('Add sources and target schema to regenerate mappings.', { variant: 'warning' });
      return;
    }
    userHasModifiedMappingsRef.current = false;
    userHasDeletedMappingRef.current = false;
    wsLengthWhenUserModifiedRef.current = 0;
    hasAutoRequestedMapping.current = false;
    setMappings([]);
    sendMappingPayload(finalPayload);
    enqueueSnackbar('Regenerating mappings...', { variant: 'success' });
  };

  const convertRowsToCSV = (columns, rows) => {
    if (!rows?.length) return '';

    const header = columns.join(',');

    const csvRows = rows.map((row) =>
      columns
        .map((col) => {
          const value = row[col];

          if (value === null || value === undefined) return '';

          // 🔥 Convert timestamps (milliseconds)
          if (typeof value === 'number' && String(value).length === 13) {
            const d = new Date(value);
            return d.toISOString().split('T')[0];
          }

          // Escape quotes
          if (typeof value === 'string') {
            return `"${value.replace(/"/g, '""')}"`;
          }

          return value;
        })
        .join(',')
    );

    return [header, ...csvRows].join('\n');
  };
  // Preview grid data formatting
  const previewGridData = useMemo(() => {
    if (!Array.isArray(previewData)) return [];

    return previewData.map((row, index) => ({
      id: row.patient_id || row.id || index, // Try common ID fields, fallback to index
      ...row,
      // Format dates if they exist
      ...(row.date_of_birth && {
        date_of_birth:
          row.date_of_birth instanceof Date
            ? row.date_of_birth.toLocaleDateString()
            : new Date(row.date_of_birth).toLocaleDateString(),
      }),
      ...(row.first_visit_date && {
        first_visit_date:
          row.first_visit_date instanceof Date
            ? row.first_visit_date.toLocaleDateString()
            : new Date(row.first_visit_date).toLocaleDateString(),
      }),
      ...(row.last_visit_date && {
        last_visit_date:
          row.last_visit_date instanceof Date
            ? row.last_visit_date.toLocaleDateString()
            : new Date(row.last_visit_date).toLocaleDateString(),
      }),
    }));
  }, [previewData]);

  const previewGridHeader = useMemo(() => {
    if (!previewData?.length) return [];

    return Object.keys(previewData[0]).map((key) => ({
      field: key,
      headerName: key.replaceAll('_', ' ').toUpperCase(),
      flex: 1,
      minWidth: 150,
    }));
  }, [previewData]);

  const content = (
    <>
      <ModalContent
        sources={sources}
        mappings={displayMappings}
        setMappings={setMappings}
        aiSuggestions={aiSuggestions}
        targetSchema={targetSchema}
        fetchedHeader={fetchedHeader}
        sourceProfileData={sourceProfileData}
        targetProfileData={targetProfileData}
        disableForm={disableForm}
        isInitialized={isInitialized}
        duckDBLoading={duckDBLoading}
        mappingLoading={wsLoading}
        totalTargetColumns={totalTargetColumns}
        existingMappings={existingMappings}
        onSourcesChange={setSources}
        onJoinsChange={setJoinsNoop}
        onAiSuggestionsChange={setAiSuggestions}
        onMappingsChange={setMappings}
        onRequestAI={handleRequestAI}
        onSaveMappings={handleSaveMappings}
        onRegenerateMappings={handleRegenerateMappings}
        previewTables={previewTables}
        previewData={previewData}
        previewError={previewError}
        previewLoading={previewLoading}
        isPreviewReady={isPreviewAvailable} // For backward compatibility
        isPreviewButtonEnabled={isPreviewButtonEnabled}
        currentSql={currentSql}
        onPreview={handlePreview}
        onDeleteMapping={(editIndex) => {
          if (editIndex == null || editIndex < 0) return;
          const current = Array.isArray(displayMappingsRef.current) ? displayMappingsRef.current : [];
          const next = current.filter((_, i) => i !== editIndex);
          wsLengthWhenUserModifiedRef.current = Array.isArray(wsMappings) ? wsMappings.length : 0;
          userHasModifiedMappingsRef.current = true;
          userHasDeletedMappingRef.current = true;
          setMappings(next);
        }}
        onSaveDirectMapping={(payload, editIndex) => {
          wsLengthWhenUserModifiedRef.current = Array.isArray(wsMappings) ? wsMappings.length : 0;
          userHasModifiedMappingsRef.current = true;
          const sourceCol =
            payload.sourceTable && payload.sourceCol
              ? `${payload.sourceTable}.${payload.sourceCol}`
              : payload.sourceCol || '';
          setMappings((prev) => {
            const p = Array.isArray(prev) ? prev : [];

            const newMapping = {
              target_column: payload.targetCol ?? '',
              mapping_type: 'DIRECT',
              source_columns: sourceCol ? [sourceCol] : [],
              confidence: '',
              mapping_state: 'auto_merge',
              possible_matches: [],
              reasoning: '',
              destination_name: payload.targetTable ?? '',
            };
            if (editIndex != null && editIndex >= 0 && editIndex < p.length) {
              return p.map((m, i) => (i === editIndex ? newMapping : m));
            }
            return [...p, newMapping];
          });
        }}
        onOpenPreviewDialog={() => {
          if (previewError) {
            enqueueSnackbar('Preview not available, query failed!', { variant: 'error' });
          } else {
            setOpenPreviewDialog(true);
          }
        }}
        onSaveJoinMapping={(payload, editIndex) => {
          wsLengthWhenUserModifiedRef.current = Array.isArray(wsMappings) ? wsMappings.length : 0;
          userHasModifiedMappingsRef.current = true;
          const src1 =
            payload.source1Table && payload.source1Col
              ? `${payload.source1Table}.${payload.source1Col}`
              : payload.source1Col || '';
          const src2 =
            payload.source2Table && payload.source2Col
              ? `${payload.source2Table}.${payload.source2Col}`
              : payload.source2Col || '';
          const source_columns =
            Array.isArray(payload.source_columns) && payload.source_columns.length >= 2
              ? payload.source_columns
              : [src1, src2].filter(Boolean);
          const onClause =
            payload.on != null && payload.on !== ''
              ? payload.on
              : src1 && src2
                ? `${payload.source1Table}.${payload.source1Col} = ${payload.source2Table}.${payload.source2Col}`
                : '';
          const newMapping = {
            target_column: payload.targetCol || '',
            mapping_type: 'JOIN',
            source_columns,
            confidence: '',
            mapping_state: 'auto_merge',
            possible_matches: [],
            join_type: (payload.joinType || 'LEFT').toUpperCase(),
            aggregate_type: payload.operation ? String(payload.operation).toUpperCase() : '',
            reasoning: '',
            destination_name: payload.targetTable || '',
            on: onClause,
            cardinality: payload.cardinality || 'many-one',
            aggregate_group_by: (Array.isArray(payload.groupBy)
              ? payload.groupBy
              : payload.groupBy
                ? [payload.groupBy]
                : []
            ).map((g) => (payload.source1Table ? `${payload.source1Table}.${g}` : g)),
            aggregate_col:
              payload.aggregateCol && payload.source2Table
                ? `${payload.source2Table}.${payload.aggregateCol}`
                : payload.aggregateCol || '',
          };
          setMappings((prev) => {
            const p = Array.isArray(prev) ? prev : [];
            if (editIndex != null && editIndex >= 0 && editIndex < p.length) {
              return p.map((m, i) => (i === editIndex ? newMapping : m));
            }
            return [...p, newMapping];
          });
        }}
      />

      <Dialog open={openPreviewDialog} onClose={() => setOpenPreviewDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Preview Data
          {previewError && <Box sx={{ color: 'error.main', fontSize: '0.875rem', ml: 2 }}>Error: {previewError}</Box>}
          <IconButton onClick={() => setOpenPreviewDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {previewError ? (
            <Box
              sx={{
                p: 3,
                bgcolor: 'error.light',
                color: 'error.contrastText',
                borderRadius: 1,
                mb: 2,
              }}
            >
              <strong>Preview Error:</strong> {previewError}
            </Box>
          ) : (
            <Box sx={{ height: 500, width: '100%' }}>
              <DataGrid
                rows={previewGridData}
                columns={previewGridHeader}
                pageSizeOptions={[15, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 15, page: 0 },
                  },
                }}
                checkboxSelection
                disableRowSelectionOnClick
                loading={previewLoading}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  )

  return (
    <>
      {
        dataModel ? (
          content
        ) :
          (
            <Dialog
              fullScreen
              open={open}
              TransitionComponent={Transition}
              sx={{
                width: '70%',
                marginLeft: '30%',
                '& .MuiDialog-paper': {
                  width: '100%',
                  height: '100vh',
                  maxHeight: '100vh',
                  margin: 0,
                },
              }}
            >
              <form
                autoComplete="off"
                onSubmit={handleFormsubmit}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100vh',
                  width: '100%',
                }}
              >
                <AppBar sx={{ position: 'relative', backgroundColor: '#fff' }}>
                  <Toolbar>
                    <IconButton edge="start" onClick={handleClose} aria-label="close" sx={{ color: '#00043C' }}>
                      <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1, color: '#00043C' }} variant="h6" component="div">
                      {nodeName}
                    </Typography>


                    <Tooltip title="Node Information" placement="left">
                      <IconButton
                        edge="start"
                        onClick={() => setSchemaInfoModalOpen(true)}
                        aria-label="node information"
                        sx={{ color: '#00043C' }}
                      >
                        <InfoOutlinedIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Refresh Schema" placement="left">
                      <IconButton
                        disabled={disableForm}
                        edge="start"
                        onClick={refreshAllState}
                        aria-label="refresh"
                        sx={{ color: '#00043C' }}
                      >
                        <RotateLeftIcon />
                      </IconButton>
                    </Tooltip>

                    <ButtonGroup variant="contained" aria-label="outlined primary button group">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleResetForm}
                        disabled={disableForm}
                        className="outlined-button-color"
                      >
                        Clear
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!disableForm}
                        className="outlined-button-color"
                        onClick={handleEdit}
                      >
                        Edit
                      </Button>
                      <Button
                        type="submit"
                        size="small"
                        variant="contained"
                        disabled={disableForm || !isInitialized || wsLoading}
                        className="button-color"
                      >
                        Submit
                      </Button>
                    </ButtonGroup>
                  </Toolbar>

                  {/* DuckDB Loading Progress */}
                  {duckDBLoading && (
                    <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />
                  )}

                  {/* DuckDB Status Message */}
                  {duckDBStatus === 'ready' && testQueryResult && (
                    <Box sx={{ px: 3, py: 1, backgroundColor: '#e8f5e9' }}>
                      <Typography variant="caption" sx={{ color: '#2e7d32' }}>
                        ✅ {testQueryResult.version} | {testQueryResult.status}
                      </Typography>
                    </Box>
                  )}

                  {duckDBError && (
                    <Box sx={{ px: 3, py: 1, backgroundColor: '#ffebee' }}>
                      <Typography variant="caption" sx={{ color: '#c62828' }}>
                        ❌ Error: {duckDBError}
                      </Typography>
                    </Box>
                  )}

                  {wsLoading && (
                    <Box sx={{ px: 3, py: 1, backgroundColor: '#e3f2fd' }}>
                      <Typography variant="caption" sx={{ color: '#1565c0' }}>
                        Please wait while AutoMapper is mapping columns…
                      </Typography>
                    </Box>
                  )}
                </AppBar>
                {content}
              </form>
            </Dialog>
          )}

      <Modal
        open={schemaInfoModalOpen}
        onClose={() => setSchemaInfoModalOpen(false)}
        aria-labelledby="auto-mapper-information-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <Box
          sx={{
            width: '70vw',
            height: '80vh',
            bgcolor: 'background.paper',
            p: 2,
            position: 'relative',
            boxShadow: 24,
            outline: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => setSchemaInfoModalOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: (theme) => theme.palette.grey[500],
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={AutoMapperInfoImage}
              alt="Auto Mapper Information"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default NodeModal_AutoMapperV2;
